import React, { useState } from "react";
import { FiShoppingCart } from "react-icons/fi";
import Select from "react-select";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { addToCart, cartData } from "../../../features/products/productSlice";
import { uid } from "../../../features/user/userSlice";
import Buttons from "../../Buttons/Buttons";
import "./Card.css";
import { Tooltip, Whisper, Button } from "rsuite";
import HelpOutlineIcon from "@rsuite/icons/HelpOutline";
import { useNavigate } from "react-router-dom";

/**
 * Component that displays the Card page
 *
 * @param {Object} props - The props object containing product information
 * @return {JSX.Element} Card component JSX
 */
export default function Card(props) {
  const dispatch = useDispatch();
  const basket = useSelector(cartData);
  const BasketItems = basket && basket.items ? basket.items : [];
  const userId = useSelector(uid);
  const navigate = useNavigate();

  const variantOptions =
    props.variants?.map((variant) => ({
      ...variant,
      value: variant.id,
      label: `${variant.name} - Rs ${variant.currentPrice}`
    })) || [];

  const [selectedVariant, setSelectedVariant] = useState(
    variantOptions.length > 0 ? variantOptions[0] : null
  );

  const tooltip = (
    <Tooltip>
      Option prices are calculated based on unit, but your final bill will
      depend on actual weight/quantity.
    </Tooltip>
  );

  const handleAddToCart = (variant) => {
    if (!variant) return;
    if (!userId) {
      navigate("/login?redirect=/products");
      return;
    }
    dispatch(
      addToCart({
        productVariantId: variant.id,
        count: 1
      })
    );
  };

  const variantInCart = selectedVariant
    ? BasketItems.find((item) => item.productVariantId === selectedVariant.id)
    : null;

  const variantStockStatus = selectedVariant.stockStatus;
  const isVariantInCart = !!variantInCart;
  const variantQuantity = variantInCart ? variantInCart.quantity : 0;

  const image = props?.cover || "/images/products/default_prod.jpg";

  return (
    <div key={props.id} className="box">
      <div className="box-content">
        <div className="product-img-container">
          <img className="product_img" src={image} alt={props.name} />
        </div>
        <div className="product-content">
          <div className="product-content-texts">
            <div className="title">
              {props.name}
              {props.variants?.length > 1 && (
                <div className="multi_variant_title">
                  <Whisper placement="top" trigger="hover" speaker={tooltip}>
                    <Button>{variantOptions[0]?.label}</Button>
                  </Whisper>
                  <Whisper placement="top" trigger="click" speaker={tooltip}>
                    <HelpOutlineIcon />
                  </Whisper>
                </div>
              )}
            </div>
            <div className="price">
              <span>
                Rs{" "}
                {selectedVariant
                  ? Number(selectedVariant.currentPrice).toLocaleString()
                  : ""}
              </span>
            </div>
          </div>
          <div>
            {variantOptions.length > 1 ? (
              <Select
                value={selectedVariant}
                onChange={setSelectedVariant}
                options={variantOptions}
                className="option_select"
              />
            ) : variantOptions.length > 0 ? (
              <span>{variantOptions[0].label}</span>
            ) : null}
          </div>
        </div>
      </div>
      {variantStockStatus === "OUT_OF_STOCK" ? (
        <div className="out-of-stock-label">Out of Stock</div>
      ) : isVariantInCart ? (
        <Buttons
          productVariantId={selectedVariant.id}
          quantity={variantQuantity}
        />
      ) : (
        <button
          onClick={() => handleAddToCart(selectedVariant)}
          className="products_button buy_button"
        >
          Buy
          <FiShoppingCart className="buy_icon" />
        </button>
      )}
    </div>
  );
}

Card.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  cover: PropTypes.string,
  variants: PropTypes.array,
  category: PropTypes.object
};
